import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, MapPin, Linkedin, Link2 } from "lucide-react";

const SUGANDHI_PORTRAIT = "https://customer-assets-lxgj4vgw.emergentagent.net/job_silver-studio-new/artifacts/54e54w3z_Screenshot%202026-07-21%20at%205.38.00%E2%80%AFPM.png";
const SUGANDHI_PORTRAIT_ALT = "https://thesilver-tree.com/wp-content/uploads/2024/02/1-about-768x1086.jpg";

const partnerDisciplines = [
    "Celebrity & influencer partnerships",
    "SEO",
    "Meta & Google ads",
    "Website design & development",
    "Performance marketing & metrics",
    "Additional design hands, when scale demands",
];

const About = () => (
    <main data-testid="about-page" className="bg-[var(--st-cream)]">
        {/* Hero */}
        <section data-testid="about-hero" className="pt-36 lg:pt-48 pb-24">
            <div className="st-container">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="eyebrow text-[var(--st-gold-deep)] mb-6"
                >
                    SilverTree Design Studio — About
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.1 }}
                    className="font-serif leading-[0.92] tracking-tighter max-w-6xl"
                    style={{ fontSize: "clamp(2.6rem, 6.5vw, 6.8rem)" }}
                >
                    Some studios build brands.<br />
                    <span className="italic text-[var(--st-gold)]">
                        SilverTree builds the brands<br />founders want to be remembered by.
                    </span>
                </motion.h1>
            </div>
        </section>

        {/* Studio intro */}
        <section data-testid="about-intro" className="pb-24 lg:pb-32">
            <div className="st-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                <div className="lg:col-span-4">
                    <p className="eyebrow text-[var(--st-gold-deep)]">01 — The studio</p>
                </div>
                <div className="lg:col-span-7 lg:col-start-6 text-lg leading-relaxed text-[var(--st-ink-soft)] space-y-6">
                    <p>
                        A boutique brand and design studio working across <strong className="text-[var(--st-ink)] font-medium">Gurgaon and Kuala Lumpur</strong>,
                        SilverTree has spent over fifteen years and fifty-plus brands answering
                        a single question: <em>what makes a brand worth remembering?</em>
                    </p>
                    <p>
                        The answer is rarely a logo. It's the clarity underneath it — the
                        strategy, the story, and the discipline to say it well, everywhere,
                        every time.
                    </p>
                </div>
            </div>
        </section>

        {/* The Studio Model */}
        <section data-testid="about-model" className="section-dark py-24 lg:py-32">
            <div className="st-container">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-16">
                    <div className="lg:col-span-5">
                        <p className="eyebrow text-[var(--st-gold)] mb-5">02 — How we're built</p>
                        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
                            Founder-led<br />by design,<br />
                            <span className="italic text-[var(--st-gold)]">not by accident.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 text-lg leading-relaxed text-[#d8d4c1] space-y-6">
                        <p>
                            SilverTree is <strong className="text-[var(--st-cream)] font-medium">founded and solely owned by Sugandhi Luthra</strong>.
                            The studio runs as a deliberately small core surrounded by a network
                            of long-term specialist associates — people Sugandhi has worked
                            alongside for years.
                        </p>
                        <p>
                            When a project calls for them, those specialists step in as partners,
                            not placeholders. It's the range of a full agency without the
                            overhead, the hand-offs, or the junior-team guesswork.
                        </p>
                        <p className="font-serif italic text-2xl text-[var(--st-gold)] pt-2">
                            You work with the founder. The right experts work with her.
                        </p>
                    </div>
                </div>

                {/* Partner disciplines list */}
                <div className="border-t border-[rgba(235,233,222,0.15)] pt-10">
                    <p className="eyebrow text-[var(--st-gold)] mb-8">The specialist network — on call when the brief needs it</p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                        {partnerDisciplines.map((d) => (
                            <li key={d} className="flex items-start gap-3 text-[#e6e2cf] text-base py-2 border-b border-[rgba(235,233,222,0.08)]">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--st-gold)] mt-2.5 shrink-0" />
                                {d}
                            </li>
                        ))}
                    </ul>
                </div>

                <p className="mt-14 text-lg text-[#d8d4c1] max-w-3xl leading-relaxed">
                    That model shapes everything the studio does — organised across three pillars.
                </p>
            </div>
        </section>

        {/* Three Pillars quick nav */}
        <section data-testid="about-pillars" className="py-20 lg:py-24 border-b border-[var(--st-line)]">
            <div className="st-container grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
                {[
                    { to: "/design", n: "01", label: "Design", color: "var(--st-green)", note: "Identities, websites, campaigns and spaces — made to last." },
                    { to: "/consult", n: "02", label: "Consult", color: "var(--st-coral)", note: "Strategy, positioning and brand management for founders." },
                    { to: "/learn", n: "03", label: "Learn", color: "var(--st-purple)", note: "Workshops, mentorship, and the studio's practice — taught." },
                ].map((p) => (
                    <Link
                        to={p.to}
                        key={p.n}
                        data-testid={`about-pillar-${p.label.toLowerCase()}`}
                        className="group block p-8 border border-[var(--st-line)] hover:bg-[var(--st-cream-soft)] transition-colors duration-500"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="w-6 h-6 mb-6" style={{ background: p.color }} />
                                <p className="eyebrow opacity-60">{p.n}</p>
                                <h3 className="font-serif text-4xl mt-1" style={{ color: p.color }}>{p.label}</h3>
                            </div>
                            <ArrowUpRight size={22} className="opacity-60 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                        </div>
                        <p className="mt-6 text-sm text-[var(--st-ink-soft)] leading-relaxed">{p.note}</p>
                    </Link>
                ))}
            </div>
        </section>

        {/* About Sugandhi */}
        <section data-testid="about-sugandhi" className="py-24 lg:py-32">
            <div className="st-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9 }}
                    className="lg:col-span-5"
                >
                    <div className="w-full">
                        <img
                            src={SUGANDHI_PORTRAIT}
                            onError={(e) => { e.currentTarget.src = SUGANDHI_PORTRAIT_ALT; }}
                            alt="Sugandhi Luthra, Founder of SilverTree Design Studio"
                            className="w-full h-auto block"
                            loading="lazy"
                        />
                    </div>
                    <div className="mt-6 border-t border-[var(--st-line)] pt-5">
                        <div className="flex items-baseline gap-4 flex-wrap">
                            <span className="font-serif italic text-2xl">Sugandhi Luthra</span>
                            <span className="eyebrow text-[var(--st-gold-deep)]">Founder & Creative Director</span>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            <a
                                href="https://www.linkedin.com/in/sugandhi-luthra-139a4b6"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="sugandhi-linkedin"
                                className="inline-flex items-center gap-2 px-3.5 py-2 border border-[var(--st-line)] hover:bg-[var(--st-ink)] hover:text-[var(--st-cream)] transition-colors text-xs uppercase tracking-widest"
                            >
                                <Linkedin size={14} /> LinkedIn
                            </a>
                            <a
                                href="https://linktr.ee/sugandhiluthra"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="sugandhi-linktree"
                                className="inline-flex items-center gap-2 px-3.5 py-2 border border-[var(--st-line)] hover:bg-[var(--st-ink)] hover:text-[var(--st-cream)] transition-colors text-xs uppercase tracking-widest"
                            >
                                <Link2 size={14} /> Portfolio
                            </a>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.15 }}
                    className="lg:col-span-6 lg:col-start-7"
                >
                    <p className="eyebrow mb-5 text-[var(--st-gold-deep)]">03 — About Sugandhi</p>
                    <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
                        Strategist,<br /> designer, and teacher —<br />
                        <span className="italic text-[var(--st-gold)]">in one person.</span>
                    </h2>
                    <div className="mt-8 text-lg leading-relaxed text-[var(--st-ink-soft)] space-y-6 max-w-2xl">
                        <p>
                            Behind the work is Sugandhi herself — a communication designer with
                            over <strong className="text-[var(--st-ink)] font-medium">seventeen years across agency, academia, and enterprise</strong>.
                            She has built brands inside advertising, taught the next generation
                            of designers as faculty, and led strategy for organisations whose
                            reach extends well beyond a single market.
                        </p>
                        <p>
                            She works the way she thinks: structured but human, rigorous but
                            warm — as comfortable with a dashboard of metrics as with the
                            emotional truth of a brand.
                        </p>
                        <p className="pt-4">
                            That combination is what founders come to SilverTree for.
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>

        {/* Positioning — between big agency and freelance */}
        <section data-testid="about-positioning" className="bg-[var(--st-cream-soft)] py-24 lg:py-32">
            <div className="st-container">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-4">
                        <p className="eyebrow text-[var(--st-gold-deep)] mb-5">04 — Why SilverTree, not someone else</p>
                        <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                            The studio that sits<br />
                            <span className="italic">between the two.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-8 border border-[var(--st-line)] bg-[var(--st-cream)]">
                            <p className="eyebrow text-[var(--st-ink-soft)] mb-3">Big agencies</p>
                            <p className="font-serif text-2xl italic leading-snug">…can feel like a machine.</p>
                        </div>
                        <div className="p-8 border border-[var(--st-line)] bg-[var(--st-cream)]">
                            <p className="eyebrow text-[var(--st-ink-soft)] mb-3">Freelancers</p>
                            <p className="font-serif text-2xl italic leading-snug">…can feel like a gamble.</p>
                        </div>
                        <div className="md:col-span-2 p-8 bg-[var(--st-ink)] text-[var(--st-cream)]">
                            <p className="eyebrow text-[var(--st-gold)] mb-3">SilverTree — deliberately between</p>
                            <p className="font-serif text-3xl leading-snug">
                                Senior attention. Specialist depth. A real stake in how things turn out.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* Closing manifesto */}
        <section data-testid="about-manifesto" className="py-24 lg:py-40">
            <div className="st-container max-w-5xl">
                <p className="eyebrow text-[var(--st-gold-deep)] mb-8">05 — The belief that runs through everything</p>
                <h2 className="font-serif leading-[0.95] tracking-tight" style={{ fontSize: "clamp(2.2rem, 5vw, 4.5rem)" }}>
                    The brands that get remembered aren't the loudest.<br />
                    <span className="italic text-[var(--st-gold)]">
                        They're the clearest, the most consistent, the most sure of who they are.
                    </span>
                </h2>
                <p className="mt-10 text-lg lg:text-xl leading-relaxed text-[var(--st-ink-soft)] max-w-3xl">
                    SilverTree exists to give founders that same certainty — to shape brands
                    with the substance to last and the craft to be noticed.
                </p>
            </div>
        </section>

        {/* Locations */}
        <section data-testid="about-locations" className="pb-24 lg:pb-28">
            <div className="st-container grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
                {[
                    {
                        city: "Gurgaon",
                        region: "Haryana · India",
                        address: "Sector 48, Sohna Road, Gurgaon",
                        note: "Our HQ since 2010. Where the team gathers for production weeks and workshops.",
                    },
                    {
                        city: "Kuala Lumpur",
                        region: "Malaysia",
                        address: "Mont Kiara, Jalan Dutamas, Kuala Lumpur",
                        note: "Our Southeast Asia studio. Where we work with hospitality, craft and lifestyle brands across the region.",
                    },
                ].map((l) => (
                    <div key={l.city} className="p-10 border border-[var(--st-line)] hover:bg-[var(--st-cream-soft)] transition-colors">
                        <div className="flex items-center gap-2 eyebrow text-[var(--st-gold-deep)]">
                            <MapPin size={14} /> {l.region}
                        </div>
                        <h3 className="font-serif text-5xl mt-3">{l.city}</h3>
                        <p className="mt-5 font-serif italic text-lg text-[var(--st-ink)]">{l.address}</p>
                        <p className="mt-4 text-[var(--st-ink-soft)] leading-relaxed">{l.note}</p>
                    </div>
                ))}
            </div>
        </section>

        {/* Tagline + CTA */}
        <section data-testid="about-cta" className="section-dark py-24 lg:py-32">
            <div className="st-container">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
                    <div>
                        <p className="eyebrow text-[var(--st-gold)] mb-4">The studio's tagline, in full</p>
                        <h3 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
                            Design. Strategise. <span className="italic text-[var(--st-gold)]">Learn.</span>
                            <br />
                            <span className="font-serif italic text-3xl md:text-4xl text-[#c8c5b3]">
                                Sometimes all on the same project.
                            </span>
                        </h3>
                    </div>
                    <div className="flex flex-wrap gap-3 shrink-0">
                        <a href="mailto:sugandhi@thesilver-tree.com" data-testid="about-email" className="btn-ghost border-[var(--st-cream)] text-[var(--st-cream)] hover:bg-[var(--st-cream)] hover:text-[var(--st-ink)]">
                            <Mail size={16} /> sugandhi@thesilver-tree.com
                        </a>
                        <Link to="/contact" data-testid="about-book-call" className="btn-primary bg-[var(--st-gold)] border-[var(--st-gold)] text-[var(--st-ink)] hover:bg-[var(--st-cream)] hover:border-[var(--st-cream)]">
                            Book a call <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    </main>
);

export default About;
